import type { PropsWithChildren } from 'react';
import React, { useContext, useMemo, useState } from 'react';
import type { UseNotificationsType } from './common/hooks/useContextNotifications';
import useContextNotifications from './common/hooks/useContextNotifications';
import type { Theme } from './common/config';
import { getConfig } from './common/config';

type AppContextType = {
  isContentDisabled: boolean,
  setContentDisabled: (flag: boolean) => any,
  useNotifications?: UseNotificationsType,
  theme: Theme
}

const AppContext = React.createContext<AppContextType>({
  isContentDisabled: false,
  // This is only to make eslint and typescript happy.
  // The function will be defined in App.tsx
  setContentDisabled: (flag: boolean): any => flag,
  theme: 'zimpel',
});

export const useNotificationContext = () => {
  const { useNotifications } = useContext(AppContext);
  if (!useNotifications) {
    throw new Error(
      'Notifications not defined',
    );
  }
  return useNotifications;
};

export const AppContextContainer: React.FC<PropsWithChildren> = ({ children }) => {
  const [isContentDisabled, setContentDisabled] = useState(false);
  const notificationsHook = useContextNotifications();
  const { theme } = getConfig();

  const appContextValue = useMemo(
    () => ({
      isContentDisabled, setContentDisabled, useNotifications: notificationsHook, theme,
    }),
    [isContentDisabled, setContentDisabled, notificationsHook, theme],
  );

  return (
    <AppContext.Provider value={appContextValue}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;
